import React, { useState, useEffect } from 'react';
import { talentScheduleService, bookingService, noteService } from '../../../api/apiService';
import '../../../App.css';
import { format, eachDayOfInterval, startOfWeek, endOfWeek } from 'date-fns';
import moment from 'moment';
import { TalentCalendarProvider, useTalentCalendar } from '../../../contexts/TalentCalendarContext';
import TalentCalendar from '../../Calendar/TalentCalendar';

// Inner component that can access the context
function TalentCalendarApiInner({ talentId, relatedTalents = [] }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [bookings, setBookings] = useState([]);

  // Use the context's data instead of local state
  const { 
    currentDate, 
    availabilities, 
    vacations, 
    awaitings, 
    secondoptions,
    loading: contextLoading 
  } = useTalentCalendar();
  const view = 'week'; // Static since we're only doing week view

  // Helper function to combine date and time
  const combineDateTime = (dateStr, timeStr) => {
    if (!timeStr) return new Date(dateStr);
    const [hours, minutes] = timeStr.split(':');
    const date = new Date(dateStr);
    date.setHours(parseInt(hours), parseInt(minutes));
    return date;
  };

  // Helper function to get color based on booking status
  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "#15a326";
      case "new order":
        return "#ffffff";
      case "requested":
        return "#FF8806";
      case "talentaccepted":
        return "#3de651";
      case "declined request":
        return "#ff3c00";
      case "optioned":
        return "#0060ff";
      case "optionSolved":
        return "#FFFF00";
      case "hold":
        return "#800080";
      case "lano":
        return "#116c11";
      case "Booked":
        return "#ADD8E6";
      default:
        return "#606060";
    }
  };

  // Helper function to get border radius based on set
  const getBorderRadius = (set) => {
    switch (set) {
      case "On Model":
        return "10px";
      case "on Produkt":
        return "0px";
      default:
        return "5px";
    }
  };

  const fetchScheduleItems = async () => {
    if (!talentId) {
      console.log("No talent ID provided, skipping fetch");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log("Fetching schedule for talentId:", talentId);
      
      // Calculate date range based on current view
      let startDate, endDate;
      if (view === 'week') {
        startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
      } else {
        startDate = currentDate;
        endDate = currentDate;
      }
      
      const weekStart = moment(startDate).format('YYYY-MM-DD');
      const weekEnd = moment(endDate).format('YYYY-MM-DD');
      
      console.log("Date range:", weekStart, "to", weekEnd);
      
      // Note: Availability, vacation, awaiting, and secondoption data is now handled by TalentCalendarContext
      // We only need to fetch notes and additional booking data if needed
      
      let filteredBookings = [];
      let scheduledNotes = [];
      
      // Fetch notes separately
      try {
        const notesResponse = await noteService.getTalentNotesByDateRange(talentId, weekStart, weekEnd);
        let notesData = [];
        if (notesResponse.data?.notes) {
          notesData = notesResponse.data.notes;
        } else if (Array.isArray(notesResponse.data)) {
          notesData = notesResponse.data;
        } else if (Array.isArray(notesResponse.notes)) {
          notesData = notesResponse.notes;
        }
        scheduledNotes = notesData || [];
        console.log('Fetched notes:', scheduledNotes.length);
      } catch (error) {
        console.error("Error fetching notes:", error);
      }
      
      // Fallback: Fetch individual talent bookings if schedule didn't include them
      if (filteredBookings.length === 0) {
        try {
          const bookingsResponse = await bookingService.getBookingsByTalentId(talentId);
          let bookingsData = [];
          if (bookingsResponse.data?.bookings) {
            bookingsData = bookingsResponse.data.bookings;
          } else if (Array.isArray(bookingsResponse.data)) {
            bookingsData = bookingsResponse.data;
          } else if (Array.isArray(bookingsResponse.bookings)) {
            bookingsData = bookingsResponse.bookings;
          }
          
          filteredBookings = bookingsData.filter(
            booking => booking.status !== "deleted"
          );
          console.log('Fetched individual talent bookings (fallback):', filteredBookings.length);
        } catch (error) {
          console.error("Error fetching individual bookings:", error);
        }
      }
      
      // Set local state (availability data comes from context now)
      setNotes(scheduledNotes);
      setBookings(filteredBookings);
      
      // Process all data into events for the calendar
      let processedItems = [];
      
      // Generate array of dates for the range
      const dates = eachDayOfInterval({ start: startDate, end: endDate });
      
      dates.forEach((dateItem) => {
        const dayOfWeek = dateItem.getDay() || 7; // Convert Sunday (0) to 7 for grid layout
        const formattedDate = moment(dateItem).format('YYYY-MM-DD');
        
        // Find all matching entries for this date (including dates within ranges)
        // Use context data instead of local variables
        const matchingAvail = availabilities.filter(entry => {
          const entryStart = moment(entry.startDate).format('YYYY-MM-DD');
          const entryEnd = moment(entry.endDate).format('YYYY-MM-DD');
          return formattedDate >= entryStart && formattedDate <= entryEnd;
        });
        
        const matchingVacation = vacations.filter(entry => {
          const entryStart = moment(entry.startDate).format('YYYY-MM-DD');
          const entryEnd = moment(entry.endDate).format('YYYY-MM-DD');
          return formattedDate >= entryStart && formattedDate <= entryEnd;
        });
        
        const matchingAwaiting = awaitings.filter(entry => {
          const entryStart = moment(entry.startDate).format('YYYY-MM-DD');
          const entryEnd = moment(entry.endDate).format('YYYY-MM-DD');
          return formattedDate >= entryStart && formattedDate <= entryEnd;
        });
        
        const matchingSecondoption = secondoptions.filter(entry => {
          const entryStart = moment(entry.startDate).format('YYYY-MM-DD');
          const entryEnd = moment(entry.endDate).format('YYYY-MM-DD');
          return formattedDate >= entryStart && formattedDate <= entryEnd;
        });
        
        // Find the latest entry across all types based on timestamp
        const allMatches = [];
        
        matchingAvail.forEach(entry => allMatches.push({ type: 'available', entry }));
        matchingVacation.forEach(entry => allMatches.push({ type: 'vacation', entry }));
        matchingAwaiting.forEach(entry => allMatches.push({ type: 'awaiting', entry }));
        matchingSecondoption.forEach(entry => allMatches.push({ type: 'secondoption', entry }));
        
        if (allMatches.length > 0) {
          // Sort by addedAt timestamp (most recent first) - this ensures the last added entry takes precedence
          // BUT FIRST: Fix any future timestamps (Firebase issue)
          const now = moment();
          const twoDaysAgo = moment().subtract(2, 'days');
          
          console.log(`🔍 Current time: ${now.format('YYYY-MM-DD HH:mm:ss')}`);
          console.log(`🔍 Two days ago: ${twoDaysAgo.format('YYYY-MM-DD HH:mm:ss')}`);
          
          allMatches.forEach(match => {
            const originalTimestamp = match.entry.addedAt;
            const entryTime = moment(originalTimestamp);
            
            console.log(`🔍 Checking ${match.type} timestamp: ${entryTime.format('YYYY-MM-DD HH:mm:ss')} vs now: ${now.format('YYYY-MM-DD HH:mm:ss')}`);
            console.log(`🔍 Is after now? ${entryTime.isAfter(now)}`);
            
            // If timestamp is in the future, it's a Firebase issue - replace with 2 days ago
            if (entryTime.isAfter(now)) {
              console.log(`🚨 FIREBASE ISSUE: Future timestamp detected for ${match.type}: ${originalTimestamp} -> Correcting to 2 days ago`);
              match.entry.addedAt = twoDaysAgo.toISOString();
              match.correctedTimestamp = true;
            }
          });
          
          allMatches.sort((a, b) => moment(b.entry.addedAt).valueOf() - moment(a.entry.addedAt).valueOf());
          
          const latestEntry = allMatches[0];
          
          // Enhanced debugging - show ALL entries with their timestamps
          if (allMatches.length > 1) {
            console.log(`🔍 DEBUG: Multiple entries found for ${formattedDate}:`);
            allMatches.forEach((match, index) => {
              const timestamp = match.entry.addedAt ? moment(match.entry.addedAt).format('YYYY-MM-DD HH:mm:ss') : 'NO TIMESTAMP';
              const isWinner = index === 0 ? '👑 WINNER' : '';
              const corrected = match.correctedTimestamp ? '🔧 CORRECTED' : '';
              console.log(`  ${index + 1}. ${match.type} - ${timestamp} ${isWinner} ${corrected}`);
            });
          }
          
          console.log(`Latest entry for ${formattedDate} is ${latestEntry.type}, added at: ${moment(latestEntry.entry.addedAt).format('YYYY-MM-DD HH:mm:ss')}`);
          
          // Add availability status based on the most recently added entry type
          switch (latestEntry.type) {
            case 'available':
              processedItems.push({
                id: `availability-${formattedDate}`,
                title: 'Available',
                start_date: dateItem,
                date: dateItem,
                gridColumn: dayOfWeek,
                gridRow: '1',
                type: 'availability',
                backgroundColor: '#2ACEF1',
                color: '#2ACEF1',
                extendedProps: {
                  isAllDay: true,
                  type: 'availability'
                }
              });
              break;
            case 'vacation':
              processedItems.push({
                id: `vacation-${formattedDate}`,
                title: 'Vacation',
                start_date: dateItem,
                date: dateItem,
                gridColumn: dayOfWeek,
                gridRow: '1',
                type: 'vacation',
                backgroundColor: '#F12A5D',
                color: '#F12A5D',
                extendedProps: {
                  isAllDay: true,
                  type: 'vacation'
                }
              });
              break;
            case 'awaiting':
              processedItems.push({
                id: `awaiting-${formattedDate}`,
                title: 'Awaiting',
                start_date: dateItem,
                date: dateItem,
                gridColumn: dayOfWeek,
                gridRow: '1',
                type: 'awaiting',
                backgroundColor: '#FFA500',
                color: '#FFA500',
                extendedProps: {
                  isAllDay: true,
                  type: 'awaiting'
                }
              });
              break;
            case 'secondoption':
              processedItems.push({
                id: `secondoption-${formattedDate}`,
                title: 'Second Option',
                start_date: dateItem,
                date: dateItem,
                gridColumn: dayOfWeek,
                gridRow: '1',
                type: 'secondoption',
                backgroundColor: '#FF69B4',
                color: '#FF69B4',
                extendedProps: {
                  isAllDay: true,
                  type: 'secondoption'
                }
              });
              break;
          }
        }
        
        // Add notes for this date
        const dayNotes = scheduledNotes.filter(note => note.date === formattedDate);
        dayNotes.forEach((note, index) => {
          processedItems.push({
            id: `note-${note.id || `${formattedDate}-${index}`}`,
            title: 'Note',
            start_date: dateItem,
            date: dateItem,
            gridColumn: dayOfWeek,
            gridRow: '2',
            type: 'note',
            backgroundColor: '#F12A5D',
            color: '#F12A5D',
            extendedProps: {
              content: note.content || note.notes,
              user: note.user,
              isAllDay: true,
              type: 'note'
            }
          });
        });
        
        // Add bookings for this date
        const dayBookings = filteredBookings.filter(booking => {
          const bookingDate = moment(booking.start_date).format('YYYY-MM-DD');
          return bookingDate === formattedDate;
        });
        
        dayBookings.forEach((booking, bookingIndex) => {
          // Debug log to see booking structure
          console.log('Processing booking:', booking);
          
          processedItems.push({
            id: `booking-${booking.id}`,
            title: `${booking.position?.label || booking.position || ''} - ${booking.client?.label || booking.client || ''}`,
            start_date: dateItem,
            date: dateItem,
            gridColumn: dayOfWeek,
            gridRow: `${bookingIndex + 3}`,
            type: 'booking',
            status: booking.status,
            backgroundColor: getStatusColor(booking.status),
            color: getStatusColor(booking.status),
            borderRadius: getBorderRadius(booking.set),
            // Map booking fields to what WeekEvent expects
            client: booking.client?.label || booking.client || 'Unknown Client',
            talent: booking.talent_name || booking.talent?.label || booking.talent || 'Unknown Talent',
            talent_name: booking.talent_name || booking.talent?.label || booking.talent || 'Unknown Talent',
            position: booking.position?.label || booking.position || 'Unknown Position',
            extendedProps: {
              ...booking,
              isAllDay: !booking.start_time && !booking.end_time
            }
          });
        });
      });

      console.log('Final processed events:', processedItems);
      setEvents(processedItems);
      
    } catch (error) {
      console.error('Error fetching schedule items:', error);
      setError('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and refetch when date, view, or context data changes
  useEffect(() => {
    fetchScheduleItems();
  }, [talentId, currentDate, view, availabilities, vacations, awaitings, secondoptions]);

  if (loading || contextLoading) {
    return <div className="loading">Loading calendar...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return <TalentCalendar events={events} />;
}

// Main wrapper component
function TalentCalendarApi({ talentId, relatedTalents = [], initialDate }) {
  return (
    <div className="talent-calendar" style={{ width: '100%' }}>
      <TalentCalendarProvider talentId={talentId}>
        <TalentCalendarApiInner 
          talentId={talentId} 
          relatedTalents={relatedTalents} 
        />
      </TalentCalendarProvider>
    </div>
  );
}

export default TalentCalendarApi; 